import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fuelingService, Fueling, FuelingStatus as ApiFuelingStatus } from '../../api/fuelings';
import { Vehicle } from '../../api/vehicles';
import { Employee } from '../../api/Employee';
import { providerService, Provider } from '../../api/provedor';
import { fuelingHistoryService, FuelingHistory } from '../../api/fueling.history';
import { tripService } from '../../api/trips';
import { getApiErrorMessage } from '../../api';
import {
  Fuel,
  Search,
  RefreshCw,
  Plus,
  Clock,
  CheckCircle2,
  ShieldCheck,
  Car,
  User,
  Building2,
  Gauge,
  CalendarDays,
  XCircle,
  Eye,
  Check,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  Info,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
} from '../../components/ui/SharedLayout';

type TipoCombustivel = 'Gasolina' | 'Etanol' | 'Diesel' | 'Flex' | 'GNV';
type FuelingStatus = 'pendente' | 'aprovado' | 'rejeitado' | 'finalizado';

const PRECOS_COMBUSTIVEL: Record<TipoCombustivel, number> = {
  Gasolina: 5.60,
  Etanol: 4.20,
  Diesel: 4.80,
  Flex: 4.90,
  GNV: 3.50,
} as const;

type FuelingWithStatus = Omit<Fueling, 'status'> & {
  status: FuelingStatus;
};

const normalizeStatus = (status?: string): FuelingStatus => {
  switch (status) {
    case 'approved':
    case 'aprovado':
      return 'aprovado';
    case 'rejected':
    case 'rejeitado':
      return 'rejeitado';
    case 'finalizado':
      return 'finalizado';
    case 'blocked':
    case 'pending':
    case 'pendente':
    default:
      return 'pendente';
  }
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const ITEMS_PER_PAGE = 20;

const AbastecimentoContent = () => {
  const { user, setPageTitle } = useAuth();
  const currentEmployeeId = user?.employee_id ? String(user.employee_id) : '';
  const [abastecimentoSelecionado, setAbastecimentoSelecionado] = useState<FuelingWithStatus | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [abastecimentos, setAbastecimentos] = useState<FuelingWithStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerId, setProviderId] = useState('');
  const [fuelingHistory, setFuelingHistory] = useState<FuelingHistory[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [histCurrentPage, setHistCurrentPage] = useState(1);

  // Form state
  const [veiculoId, setVeiculoId] = useState('');
  const [motoristaId, setMotoristaId] = useState('');
  const [data, setData] = useState('');
  const [tipoCombustivel, setTipoCombustivel] = useState<TipoCombustivel | ''>('');
  const [quantidade, setQuantidade] = useState<number>(0);
  const [valorTotal, setValorTotal] = useState<number>(0);
  const [kmAtual, setKmAtual] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Boleto modal state
  const [isBoletoModalOpen, setIsBoletoModalOpen] = useState(false);
  const [boletoFile, setBoletoFile] = useState<File | null>(null);
  const [boletoPreview, setBoletoPreview] = useState<string | null>(null);
  const [finishingId, setFinishingId] = useState<number | null>(null);

  useEffect(() => {
    setPageTitle('Meus Abastecimentos');
  }, [setPageTitle]);

  const openFinishModal = (id: number) => {
    setFinishingId(id);
    setIsBoletoModalOpen(true);
  };
  const closeFinishModal = () => {
    setFinishingId(null);
    setIsBoletoModalOpen(false);
    setBoletoFile(null);
    setBoletoPreview(null);
    setError(null);
  };
  const handleBoletoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBoletoFile(file);
    setBoletoPreview(URL.createObjectURL(file));
  };
  useEffect(() => {
    return () => {
      if (boletoPreview) {
        URL.revokeObjectURL(boletoPreview);
      }
    };
  }, [boletoPreview]);

  const validateFuelingForm = () => {
    if (!veiculoId || !motoristaId || !tipoCombustivel || !providerId || !data) {
      return 'Preencha todos os campos obrigatórios corretamente.';
    }

    if (!Number.isFinite(quantidade) || quantidade <= 0) {
      return 'Informe uma quantidade de combustível maior que zero.';
    }

    if (!Number.isFinite(valorTotal) || valorTotal <= 0) {
      return 'O valor total do abastecimento precisa ser maior que zero.';
    }

    if (!Number.isInteger(kmAtual) || kmAtual < 0) {
      return 'Informe uma quilometragem válida.';
    }

    if (kmAtual > 2147483647) {
      return 'Quilometragem muito alta! O valor máximo permitido é 2.147.483.647.';
    }

    return null;
  };

  const handleFinishFueling = async () => {
    if (!boletoFile || finishingId === null) {
      setError('Selecione a foto do boleto para finalizar o abastecimento.');
      return;
    }
    const fueling = abastecimentos.find(ab => ab.id === finishingId);
    if (!fueling) return;

    const validProviderId = fueling.provider_id;

    setSubmitting(true);
    setError(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target?.result as string);
        reader.onerror = () => reject(new Error('Falha ao ler a imagem do boleto.'));
        reader.readAsDataURL(boletoFile);
      });

      const payload = {
        vehicle_id: Number(fueling.vehicle_id) || 0,
        employee_id: Number(fueling.employee_id) || 0,
        fuel_type: fueling.fuel_type || '',
        amount: Number(fueling.amount) || 0,
        cost_per_liter: Number(fueling.cost_per_liter) || 0,
        total_cost: Number(fueling.total_cost) || 0,
        odometer_reading: fueling.odometer_reading ? Number(fueling.odometer_reading) : undefined,
        invoice_number: fueling.invoice_number || undefined,
        notes: fueling.notes || undefined,
        fueling_time: fueling.fueling_time,
        status: 'finalizado' as ApiFuelingStatus,
        provider_id: validProviderId,
        boleto_photo: base64,
      };

      await fuelingService.updateFueling(finishingId, payload);
      setAbastecimentos(abs => abs.filter(ab => ab.id !== finishingId));
      const updatedHistory = await fuelingHistoryService.getAllFuelingHistory();
      const userHistory = updatedHistory.filter(
        (fuelingHistoryItem) => fuelingHistoryItem.employee_id === user?.employee_id,
      );
      setFuelingHistory(userHistory);
      closeFinishModal();
      setHistCurrentPage(1);
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(
          err,
          'Erro ao finalizar abastecimento. Verifique sua conexão e tente novamente.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const historicoAbastecimentos = fuelingHistory;
  // Only show non-finished fuelings in the main table
  const ativosAbastecimentos = abastecimentos.filter(ab => ab.status !== 'finalizado');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!user?.employee_id) {
      setAbastecimentos([]);
      setVehicles([]);
      setEmployees([]);
      setProviders([]);
      setFuelingHistory([]);
      setError('Seu usuário não está vinculado a um funcionário.');
      setLoading(false);
      return;
    }

    try {
      const [fuelings, trips, providers, history] = await Promise.all([
        fuelingService.getAllFuelings(),
        tripService.getAll(),
        providerService.getAll(),
        fuelingHistoryService.getAllFuelingHistory(),
      ]);
      const userFuelings = fuelings
        .filter((fueling) => fueling.employee_id === user.employee_id)
        .map((fueling) => ({
          ...fueling,
          status: normalizeStatus(fueling.status),
        }));
      const userHistory = history.filter(
        (fuelingHistoryItem) => fuelingHistoryItem.employee_id === user.employee_id,
      );
      const userVehicles = Array.from(
        new Map(
          trips
            .filter((trip) => trip.employee_id === user.employee_id && trip.vehicle)
            .map((trip) => [trip.vehicle!.id, trip.vehicle!]),
        ).values(),
      );
      const currentEmployee =
        userHistory.find((item) => item.employee)?.employee ||
        ({
          id: user.employee_id,
          name: user.name,
          cpf: user.cpf || '',
          cargo: '',
          email: user.email,
          telefone: user.phone || '',
          setor: '',
          status: 'ativo',
          created_at: '',
          updated_at: '',
          datadeadimissão: '',
        } as unknown as Employee);

      setAbastecimentos(userFuelings);
      setVehicles(userVehicles);
      setEmployees([currentEmployee]);
      setMotoristaId(currentEmployeeId);
      setProviders(providers);
      setFuelingHistory(userHistory);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(getApiErrorMessage(err, 'Erro ao carregar os dados de abastecimento.'));
    } finally {
      setLoading(false);
    }
  }, [currentEmployeeId, user]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filteredAbastecimentos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return ativosAbastecimentos.filter((abastecimento) => {
      if (!term) return true;
      const vehicle = vehicles.find(v => v.id === abastecimento.vehicle_id);
      const provider = providers.find(p => p.id === abastecimento.provider_id);
      return (
        (vehicle?.brand || '').toLowerCase().includes(term) ||
        (vehicle?.license_plate || '').toLowerCase().includes(term) ||
        abastecimento.fuel_type.toLowerCase().includes(term) ||
        abastecimento.amount.toString().includes(term) ||
        abastecimento.total_cost.toString().includes(term) ||
        (provider?.name || '').toLowerCase().includes(term) ||
        (abastecimento.invoice_number && abastecimento.invoice_number.toLowerCase().includes(term)) ||
        abastecimento.status.toLowerCase().includes(term)
      );
    });
  }, [ativosAbastecimentos, searchTerm, vehicles, providers]);

  const ativosPages = Math.max(1, Math.ceil(filteredAbastecimentos.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > ativosPages) setCurrentPage(1);
  }, [filteredAbastecimentos.length, currentPage, ativosPages]);

  const { paginatedAtivos, ativosRange } = useMemo(() => {
    const total = filteredAbastecimentos.length;
    const safePage = Math.min(currentPage, ativosPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      paginatedAtivos: filteredAbastecimentos.slice(startIndex, endIndex),
      ativosRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredAbastecimentos, currentPage, ativosPages]);

  const histPages = Math.max(1, Math.ceil(historicoAbastecimentos.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (histCurrentPage > histPages) setHistCurrentPage(1);
  }, [historicoAbastecimentos.length, histCurrentPage, histPages]);

  const { paginatedHist, histRange } = useMemo(() => {
    const total = historicoAbastecimentos.length;
    const safePage = Math.min(histCurrentPage, histPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      paginatedHist: historicoAbastecimentos.slice(startIndex, endIndex),
      histRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [historicoAbastecimentos, histCurrentPage, histPages]);

  const getPageNumbers = (current: number, totalPages: number) => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, current - 1);
    const right = Math.min(totalPages - 1, current + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const renderStatusBadge = (status: FuelingStatus) => {
    switch (status) {
      case 'aprovado':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Aprovado
          </Badge>
        );
      case 'rejeitado':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            Rejeitado
          </Badge>
        );
      case 'finalizado':
        return (
          <Badge tone="slate" className="gap-1.5">
            <ShieldCheck className="h-3 w-3 stroke-[2]" />
            Finalizado
          </Badge>
        );
      case 'pendente':
      default:
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            Pendente
          </Badge>
        );
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const iniciarRegistro = () => {
    setError(null);
    limparFormulario();
    setIsModalOpen(true);
  };

  const limparFormulario = () => {
    setVeiculoId('');
    setMotoristaId(currentEmployeeId);
    setData('');
    setTipoCombustivel('');
    setQuantidade(0);
    setValorTotal(0);
    setKmAtual(0);
    setProviderId('');
    setAbastecimentoSelecionado(null);
  };

  const cancelarOperacao = () => {
    setError(null);
    limparFormulario();
    setIsModalOpen(false);
  };

  const registrarAbastecimento = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationMessage = validateFuelingForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    const newFueling = {
      vehicle_id: Number(veiculoId),
      employee_id: Number(motoristaId),
      fuel_type: tipoCombustivel,
      amount: quantidade,
      cost_per_liter: tipoCombustivel ? PRECOS_COMBUSTIVEL[tipoCombustivel] : 0,
      total_cost: valorTotal,
      odometer_reading: kmAtual || undefined,
      invoice_number: undefined,
      notes: undefined,
      status: 'blocked' as ApiFuelingStatus,
      provider_id: Number(providerId),
      fueling_time: data ? new Date(`${data}T12:00:00`).toISOString() : undefined,
    };
    setSubmitting(true);
    setError(null);
    try {
      const fueling = await fuelingService.createFueling(newFueling);
      setAbastecimentos([...abastecimentos, { ...fueling, status: normalizeStatus(fueling.status) }]);
      limparFormulario();
      setIsModalOpen(false);
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(
          err,
          'Erro ao registrar abastecimento. Verifique sua conexão e tente novamente.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7 flex flex-col gap-6">
        <PageHeader title="Meus Abastecimentos" hideTitleInBody />

        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between mb-1">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Operações — Abastecimentos
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Registre e acompanhe
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                os seus abastecimentos
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button
              type="button"
              variant="primary"
              onClick={iniciarRegistro}
              disabled={loading || vehicles.length === 0}
              className="h-10 px-4"
            >
              <Plus className="h-4 w-4 stroke-[2]" />
              Novo Abastecimento
            </Button>
            <Button variant="secondary" onClick={() => void fetchData()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {vehicles.length === 0 && !loading && !error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-800">
            <div className="flex items-start gap-2">
              <Info className="h-[18px] w-[18px] shrink-0 mt-[1px] stroke-[1.9]" />
              <div>Nenhum veículo disponível para registrar abastecimento no seu escopo atual.</div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        {/* ============ SECTION ATIVOS ============ */}
        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.6)]">
                <Fuel className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Abastecimentos
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {loading
                    ? 'Carregando registros...'
                    : ativosRange.total === 0
                      ? 'Nenhum abastecimento ativo'
                      : `${ativosRange.start} a ${ativosRange.end} de ${ativosRange.total} abastecimentos ativos (pendentes/aprovados)`}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-fuelings"
                placeholder="Pesquisar veículo, fornecedor, combustível, NF..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando abastecimentos...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Motorista</Th>
                      <Th>Fornecedor</Th>
                      <Th>Data</Th>
                      <Th>Combustível</Th>
                      <Th>Litros</Th>
                      <Th>Valor</Th>
                      <Th>Status</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {ativosRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum abastecimento ativo encontrado.
                        </td>
                      </tr>
                    ) : (
                      paginatedAtivos.map((abastecimento) => {
                        const vehicle = vehicles.find(v => v.id === abastecimento.vehicle_id);
                        const employee = employees.find(e => e.id === abastecimento.employee_id);
                        const provider = providers.find(p => p.id === abastecimento.provider_id);
                        return (
                          <Tr key={abastecimento.id}>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-500 via-blue-500 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.6)]">
                                  <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900">
                                    {vehicle?.brand || `Veículo #${abastecimento.vehicle_id}`}
                                  </div>
                                  <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500 truncate">
                                    {vehicle?.license_plate || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                                  <User className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900">
                                    {(() => {
                                      if (!employee) return `Motorista #${abastecimento.employee_id}`;
                                      const [first, second] = employee.name.split(' ');
                                      return `${first} ${second || ''}`;
                                    })()}
                                  </div>
                                  <div className="mt-0.5 text-[12px] text-slate-500 truncate">
                                    {employee?.cpf || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="truncate font-medium text-slate-700" title={provider?.name}>
                                  {provider?.name || `#${abastecimento.provider_id}`}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                  {formatDate(abastecimento.fueling_time || abastecimento.created_at)}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1 rounded-md bg-orange-50 px-2.5 py-1 text-[12px] font-bold text-orange-700 ring-1 ring-inset ring-orange-200">
                                <Fuel className="h-3 w-3 stroke-[1.9]" />
                                {abastecimento.fuel_type || '-'}
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <Gauge className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-semibold text-slate-700 tabular-nums">
                                  {abastecimento.amount?.toLocaleString('pt-BR') || 0}L
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <span className="text-[13px] font-bold text-emerald-700 tabular-nums">
                                {formatCurrency(abastecimento.total_cost)}
                              </span>
                            </Td>
                            <Td>
                              {renderStatusBadge(abastecimento.status)}
                            </Td>
                            <Td>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setAbastecimentoSelecionado(abastecimento)}
                                  className="!h-8 !px-2"
                                  title="Visualizar detalhes"
                                >
                                  <Eye className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                                {abastecimento.status === 'aprovado' && (
                                  <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    onClick={() => openFinishModal(abastecimento.id)}
                                    className="!h-8 !px-3 !bg-emerald-600 hover:!bg-emerald-700 text-[12px] font-bold gap-1"
                                    title="Finalizar abastecimento"
                                  >
                                    <Check className="h-3.5 w-3.5 stroke-[2]" />
                                    Finalizar
                                  </Button>
                                )}
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && ativosRange.total > 0 && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{ativosRange.start}-{ativosRange.end}</span> de <span className="font-semibold text-slate-700">{ativosRange.total}</span> abastecimentos ativos
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers(currentPage, ativosPages).map((n, idx) =>
                          n === '...' ? (
                            <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center text-slate-500 text-[13px]">
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(ativosPages, p + 1))}
                        disabled={currentPage === ativosPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(ativosPages)}
                        disabled={currentPage === ativosPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>

        {/* ============ SECTION HISTÓRICO ============ */}
        {historicoAbastecimentos.length > 0 && (
          <SectionCard>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-slate-500 via-slate-600 to-slate-800 text-white shadow-[0_6px_16px_-8px_rgba(71,85,105,0.55)]">
                  <FileText className="h-[18px] w-[18px] stroke-[1.9]" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Histórico de Abastecimentos Finalizados
                  </h2>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    {histRange.total === 0
                      ? 'Sem registros finalizados'
                      : `${histRange.start} a ${histRange.end} de ${histRange.total} abastecimentos finalizados`}
                  </p>
                </div>
              </div>
              <div className="hidden sm:block w-[360px] shrink-0" />
            </CardHeader>

            <CardBody className="!py-3">
              <ModernTable>
                <Thead>
                  <Tr>
                    <Th>Veículo</Th>
                    <Th>Motorista</Th>
                    <Th>Data</Th>
                    <Th>Combustível</Th>
                    <Th>Litros</Th>
                    <Th>Valor</Th>
                    <Th>Fornecedor</Th>
                    <Th>Boleto</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedHist.map((f) => {
                    const vehicle = vehicles.find(v => v.id === f.vehicle_id);
                    const employee = employees.find(e => e.id === f.employee_id);
                    const provider = providers.find(p => p.id === f.provider_id);
                    const boletoUrl = (f.boleto_photo && (f.boleto_photo as unknown as { url?: string }))?.url
                      || (typeof f.boleto_photo === 'string' ? f.boleto_photo : null);
                    return (
                      <Tr key={f.id}>
                        <Td>
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 min-w-[32px] shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 via-blue-500 to-blue-700 text-white">
                              <Car className="h-[14px] w-[14px] stroke-[1.9]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-semibold text-slate-900 text-[13px]">
                                {vehicle?.brand || `#${f.vehicle_id}`}
                              </div>
                              <div className="mt-0.5 font-mono text-[11px] text-slate-500 truncate">
                                {vehicle?.license_plate || '-'}
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <span className="font-medium text-slate-700 truncate" title={employee?.name}>
                            {employee?.name || `#${f.employee_id}`}
                          </span>
                        </Td>
                        <Td>
                          <span className="font-medium tabular-nums text-slate-700 text-[12.5px]">
                            {formatDate(f.fueling_time || f.created_at)}
                          </span>
                        </Td>
                        <Td>
                          <div className="inline-flex items-center gap-1 rounded-md bg-orange-50 px-2 py-0.5 text-[11.5px] font-bold text-orange-700 ring-1 ring-inset ring-orange-200">
                            <Fuel className="h-2.5 w-2.5 stroke-[1.9]" />
                            {f.fuel_type}
                          </div>
                        </Td>
                        <Td>
                          <span className="font-semibold tabular-nums text-slate-700 text-[12.5px]">
                            {f.amount}L
                          </span>
                        </Td>
                        <Td>
                          <span className="text-[12.5px] font-bold text-emerald-700 tabular-nums">
                            {formatCurrency(f.total_cost)}
                          </span>
                        </Td>
                        <Td>
                          <span className="font-medium text-slate-700 truncate" title={provider?.name}>
                            {provider?.name || `#${f.provider_id}`}
                          </span>
                        </Td>
                        <Td>
                          {boletoUrl ? (
                            <img
                              src={boletoUrl}
                              alt="Boleto"
                              className="h-10 w-14 object-cover rounded border border-slate-200"
                            />
                          ) : (
                            <span className="text-[12px] font-medium text-slate-400">Sem foto</span>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </ModernTable>

              {histRange.total > 0 && (
                <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-[13px] font-medium text-slate-500">
                    Mostrando <span className="font-semibold text-slate-700">{histRange.start}-{histRange.end}</span> de <span className="font-semibold text-slate-700">{histRange.total}</span> finalizados
                  </div>
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setHistCurrentPage(1)}
                      disabled={histCurrentPage === 1}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronsLeft className="h-3.5 w-3.5 stroke-[1.8]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setHistCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={histCurrentPage === 1}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-3.5 w-3.5 stroke-[1.8]" />
                    </button>
                    <div className="inline-flex items-center gap-1 mx-1">
                      {getPageNumbers(histCurrentPage, histPages).map((n, idx) =>
                        n === '...' ? (
                          <span key={`hdots-${idx}`} className="inline-flex h-8 w-8 items-center justify-center text-slate-500 text-[12px]">
                            …
                          </span>
                        ) : (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setHistCurrentPage(n)}
                            className={`inline-flex h-8 min-w-[32px] cursor-pointer items-center justify-center rounded-md px-2 text-[12px] font-semibold transition-all ${
                              n === histCurrentPage
                                ? 'bg-blue-600 text-white'
                                : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                          >
                            {n}
                          </button>
                        ),
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setHistCurrentPage((p) => Math.min(histPages, p + 1))}
                      disabled={histCurrentPage === histPages}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-3.5 w-3.5 stroke-[1.8]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setHistCurrentPage(histPages)}
                      disabled={histCurrentPage === histPages}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronsRight className="h-3.5 w-3.5 stroke-[1.8]" />
                    </button>
                  </div>
                </div>
              )}
            </CardBody>
          </SectionCard>
        )}

        {/* ===== MODAL NOVO ABASTECIMENTO ===== */}
        {isModalOpen && (
          <Modal
            title="Registrar Abastecimento"
            onClose={cancelarOperacao}
            maxWidthClass="max-w-3xl"
            footer={
              <Fragment>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={cancelarOperacao}
                  disabled={submitting}
                  className="h-10 px-5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="form-abastecimento"
                  variant="primary"
                  disabled={submitting}
                  className="h-10 px-5"
                >
                  {submitting ? 'Registrando...' : 'Registrar Abastecimento'}
                </Button>
              </Fragment>
            }
          >
            {error && (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {error}
              </div>
            )}
            <form onSubmit={registrarAbastecimento} id="form-abastecimento">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="veiculo" className="text-[13px] font-semibold text-slate-700">
                    Veículo <span className="text-rose-600">*</span>
                  </label>
                  <select
                    id="veiculo"
                    value={veiculoId}
                    onChange={e => setVeiculoId(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    required
                  >
                    <option value="">Selecione um veículo</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.brand || '(Sem marca)'} - {v.license_plate || '(Sem placa)'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="motorista" className="text-[13px] font-semibold text-slate-700">
                    Motorista <span className="text-rose-600">*</span>
                  </label>
                  <select
                    id="motorista"
                    value={motoristaId}
                    onChange={e => setMotoristaId(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-300 bg-slate-50 px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 cursor-not-allowed opacity-80"
                    required
                    disabled
                  >
                    <option value="">Selecione um motorista</option>
                    {employees.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="data" className="text-[13px] font-semibold text-slate-700">
                    Data <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="date"
                    id="data"
                    value={data}
                    onChange={(e) => setData(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="tipoCombustivel" className="text-[13px] font-semibold text-slate-700">
                    Tipo de Combustível <span className="text-rose-600">*</span>
                  </label>
                  <select
                    id="tipoCombustivel"
                    value={tipoCombustivel}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || Object.keys(PRECOS_COMBUSTIVEL).includes(value)) {
                        const newTipo = value as TipoCombustivel | '';
                        setTipoCombustivel(newTipo);
                        if (newTipo && quantidade > 0) {
                          setValorTotal(quantidade * PRECOS_COMBUSTIVEL[newTipo]);
                        }
                      }
                    }}
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    required
                  >
                    <option value="">Selecione o combustível</option>
                    {Object.keys(PRECOS_COMBUSTIVEL).map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {tipo} - {formatCurrency(PRECOS_COMBUSTIVEL[tipo as TipoCombustivel])}/L
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="quantidade" className="text-[13px] font-semibold text-slate-700">
                    Quantidade (Litros) <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="number"
                    id="quantidade"
                    value={quantidade === 0 ? '' : quantidade}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setQuantidade(0);
                        setValorTotal(0);
                      } else {
                        const num = parseFloat(value);
                        setQuantidade(num);
                        if (tipoCombustivel) {
                          setValorTotal(num * PRECOS_COMBUSTIVEL[tipoCombustivel as TipoCombustivel]);
                        }
                      }
                    }}
                    min="0"
                    step="0.1"
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    required
                    style={{ appearance: 'textfield' }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="valorTotal" className="text-[13px] font-semibold text-slate-700">
                    Valor Total
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13.5px] font-bold text-emerald-700 tabular-nums">
                      R$
                    </span>
                    <input
                      type="text"
                      id="valorTotal"
                      value={valorTotal === 0 ? '0,00' : formatCurrency(valorTotal).replace('R$', '').trim()}
                      readOnly
                      className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-[13.5px] font-semibold text-emerald-700 tabular-nums"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="kmAtual" className="text-[13px] font-semibold text-slate-700">
                    Quilometragem Atual <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="number"
                    id="kmAtual"
                    value={kmAtual === 0 ? '' : kmAtual}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setKmAtual(0);
                      } else {
                        setKmAtual(parseInt(value, 10) || 0);
                      }
                    }}
                    min="0"
                    max="2147483647"
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 tabular-nums"
                    required
                    style={{ appearance: 'textfield' }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="provider" className="text-[13px] font-semibold text-slate-700">
                    Fornecedor <span className="text-rose-600">*</span>
                  </label>
                  <select
                    id="provider"
                    value={providerId}
                    onChange={e => setProviderId(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    required
                  >
                    <option value="">Selecione um fornecedor</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </form>
          </Modal>
        )}

        {/* ===== MODAL VISUALIZAR DETALHES ===== */}
        {abastecimentoSelecionado && (
          <Modal
            title="Detalhes do Abastecimento"
            onClose={() => setAbastecimentoSelecionado(null)}
            maxWidthClass="max-w-2xl"
            footer={
              <Button
                type="button"
                variant="secondary"
                onClick={() => setAbastecimentoSelecionado(null)}
                className="h-10 px-5"
              >
                Voltar
              </Button>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Veículo</span>
                  <span className="text-slate-900">
                    {(() => {
                      const vehicle = vehicles.find(v => v.id === abastecimentoSelecionado.vehicle_id);
                      return vehicle
                        ? <><span className="font-semibold">{vehicle.brand}</span><span className="block text-xs text-slate-500 font-mono mt-0.5">{vehicle.license_plate}</span></>
                        : `#${abastecimentoSelecionado.vehicle_id}`;
                    })()}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Motorista</span>
                  <span className="text-slate-900">
                    {(() => {
                      const employee = employees.find(e => e.id === abastecimentoSelecionado.employee_id);
                      if (employee) {
                        const [first, second] = employee.name.split(' ');
                        return <><span className="font-semibold">{first} {second || ''}</span><span className="block text-xs text-slate-500 mt-0.5">{employee.cpf}</span></>;
                      }
                      return `#${abastecimentoSelecionado.employee_id}`;
                    })()}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Status</span>
                  <div>{renderStatusBadge(abastecimentoSelecionado.status)}</div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Data</span>
                  <span className="text-slate-900 font-medium tabular-nums">
                    {formatDate(abastecimentoSelecionado.fueling_time || abastecimentoSelecionado.created_at)}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Tipo de Combustível</span>
                  <span className="text-slate-900 font-medium">{abastecimentoSelecionado.fuel_type}</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Quantidade</span>
                  <span className="text-slate-900 font-semibold tabular-nums">{abastecimentoSelecionado.amount}L</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Valor Total</span>
                  <span className="text-slate-900 font-bold tabular-nums text-emerald-700 text-[15px]">
                    {formatCurrency(abastecimentoSelecionado.total_cost)}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Quilometragem</span>
                  <span className="text-slate-900 font-medium tabular-nums">
                    {abastecimentoSelecionado.odometer_reading ? `${abastecimentoSelecionado.odometer_reading} km` : '-'}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5 md:col-span-2">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Fornecedor</span>
                  <span className="text-slate-900 font-medium">
                    {(() => {
                      const provider = providers.find(p => p.id === abastecimentoSelecionado.provider_id);
                      return provider ? provider.name : `#${abastecimentoSelecionado.provider_id}`;
                    })()}
                  </span>
                </div>
              </div>
          </Modal>
        )}

        {/* ===== MODAL FINALIZAR (BOLETO) ===== */}
        {isBoletoModalOpen && (
          <Modal
            title="Finalizar Abastecimento — Enviar Boleto"
            onClose={closeFinishModal}
            maxWidthClass="max-w-2xl"
            footer={
              <Fragment>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeFinishModal}
                  disabled={submitting}
                  className="h-10 px-5"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => void handleFinishFueling()}
                  disabled={!boletoFile || submitting}
                  className="h-10 px-5 !bg-emerald-600 hover:!bg-emerald-700"
                >
                  {submitting ? 'Enviando...' : 'Finalizar Abastecimento'}
                </Button>
              </Fragment>
            }
          >
            <div className="flex flex-col gap-4">
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                  {error}
                </div>
              )}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <div className="flex items-start gap-2">
                  <Info className="h-[18px] w-[18px] shrink-0 mt-[1px] stroke-[1.9]" />
                  <div>
                    <p className="font-semibold mb-0.5">Finalização do abastecimento</p>
                    <p className="text-[13px] text-emerald-800/90">
                      Anexe a foto do boleto/comprovante do posto para concluir. Após envio o abastecimento entrará no histórico finalizado.
                    </p>
                  </div>
                </div>
              </div>
              <label
                htmlFor="boleto-upload"
                className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 px-6 py-8 text-center transition-all hover:border-emerald-400 hover:bg-emerald-50/50"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200">
                  <ImageIcon className="h-[22px] w-[22px] stroke-[1.8] text-emerald-600" />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-slate-900">Clique para selecionar a foto do boleto</p>
                  <p className="mt-1 text-[12.5px] text-slate-500">PNG, JPG, JPEG — comprovante do posto</p>
                </div>
                <input
                  id="boleto-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleBoletoChange}
                  className="hidden"
                />
              </label>
              {boletoPreview && (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  <img
                    src={boletoPreview}
                    alt="Pré-visualização do boleto"
                    className="mx-auto max-h-72 w-full object-contain"
                  />
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default AbastecimentoContent;
